import { useEffect, useState, type ReactNode } from 'react';
import { FileCheck2, Plus, Upload, WandSparkles } from 'lucide-react';
import { AppChrome } from '@/components/AppChrome';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useFileUpload } from '@/lib/platform/uploads';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { feature, ui, useFeature, useOverlayOpen } from '@/app/context';
import { useClient, useClientActions, useClientRef } from './state';

type SetupClient = { clientId: string; clientName?: string | null; fiduciaryName?: string | null };

export function SetupPage() {
  const access = useSurfaceAccess(SURFACES['filing-setup']);
  if (access === 'waiting') return <PageMessage title="Loading access" detail="Checking access to Filing Setup." />;
  if (access === 'unavailable')
    return <PageMessage title="Access is unavailable" detail="Filing Setup could not confirm access right now." />;
  if (access === 'denied')
    return <PageMessage title="Access denied" detail="This person has no access to this screen." />;
  return <SetupContent />;
}

function SetupContent() {
  const clientRef = useClientRef();
  const clientActions = useClientActions();
  const clients = useClient((snapshot) => snapshot.context.items);
  const clientState = useClient((snapshot) => snapshot.value);
  const clientError = useClient((snapshot) => snapshot.context.error);
  const ruleRef = feature('rule-package');
  const ruleSnapshot = useFeature('rule-package', (snapshot) => snapshot);
  const filingRef = feature('filing');
  const clientDialogOpen = useOverlayOpen('openClientDialog');
  const packageDialogOpen = useOverlayOpen('publishRulePackageDialog');
  const filingDialogOpen = useOverlayOpen('openFilingDialog');
  const [clientName, setClientName] = useState('');
  const [fiduciaryName, setFiduciaryName] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [version, setVersion] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedRulePackageId, setSelectedRulePackageId] = useState('');
  const [publishError, setPublishError] = useState('');
  const upload = useFileUpload();
  const isClientLoading = clientState.viewClients === 'loading';
  const isClientReady = clientState.viewClients === 'ready';
  const packageItems = ruleSnapshot.context.items;
  const packageState = ruleSnapshot.value.viewRulePackages;

  useEffect(() => {
    clientRef.send({ type: 'client.load-viewClients' });
    ruleRef.send({ type: 'rule-package.load-viewRulePackages' });
  }, [clientRef, ruleRef]);

  const openClient = () => {
    if (!clientName.trim() || !fiduciaryName.trim()) return;
    clientActions.open({ clientName: clientName.trim(), fiduciaryName: fiduciaryName.trim() });
  };

  const publishPackage = () => {
    const parsedTaxYear = Number(taxYear);
    if (!Number.isFinite(parsedTaxYear)) {
      setPublishError('Enter a valid tax year.');
      return;
    }
    if (!version.trim() || upload.status !== 'stored' || !upload.ref) {
      setPublishError('Choose a stored rule package file before publishing.');
      return;
    }
    setPublishError('');
    ruleRef.send({
      type: 'rule-package.publish',
      input: { taxYear: parsedTaxYear, version: version.trim(), fileRef: upload.ref },
    });
  };

  const openFiling = () => {
    if (!selectedClientId || !selectedRulePackageId) return;
    filingRef.send({
      type: 'filing.open',
      input: { clientId: selectedClientId, rulePackageId: selectedRulePackageId },
    });
  };

  return (
    <AppChrome
      title="Filing Setup"
      description="Select the Client and IRS Rule Package for a traceable tax-year Filing."
    >
      <div className="grid gap-5 xl:grid-cols-2">
        <RecordCard
          title="Clients"
          description="Estate and trust Clients ready for Filing work."
          action={
            <Button size="sm" onClick={() => clientActions.openDialog()} data-jr-key="open-client-open">
              <Plus /> Open Client
            </Button>
          }
        >
          {!isClientReady && isClientLoading && <StateText text="Loading Clients…" />}
          {!isClientReady && clientState.viewClients === 'error' && (
            <StateText
              text={clientError ?? 'Clients could not be loaded.'}
              action={
                <Button size="sm" variant="outline" onClick={() => clientActions.loadViewClients()}>
                  Retry
                </Button>
              }
            />
          )}
          {isClientReady && clients.length === 0 && (
            <StateText text="No Clients recorded yet. Open a Client to begin Filing work." />
          )}
          {isClientReady && clients.length > 0 && (
            <div className="divide-y divide-border">
              {clients.map((client) => (
                <ClientRow key={client.clientId} client={client} />
              ))}
            </div>
          )}
        </RecordCard>

        <RecordCard
          title="IRS Rule Packages"
          description="Published tax-year packages available for selection."
          action={
            <Button
              size="sm"
              onClick={() => ruleRef.send({ type: 'rule-package.open-dialog' })}
              data-jr-key="publish-rule-package-open"
            >
              <Upload /> Publish Package
            </Button>
          }
        >
          {packageState === 'loading' && <StateText text="Loading Rule Packages…" />}
          {packageState === 'error' && (
            <StateText
              text="Rule Packages could not be loaded."
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => ruleRef.send({ type: 'rule-package.load-viewRulePackages' })}
                >
                  Retry
                </Button>
              }
            />
          )}
          {packageState === 'ready' && packageItems.length === 0 && (
            <StateText text="No Rule Packages are available yet." />
          )}
          {packageState === 'ready' && packageItems.length > 0 && (
            <div className="divide-y divide-border">
              {packageItems.map((item) => (
                <div key={item.rulePackageId} className="flex items-center justify-between py-3">
                  <span className="font-medium">{item.taxYear ?? 'Tax year unavailable'}</span>
                  <span className="text-sm text-muted-foreground">Version {item.version ?? 'unavailable'}</span>
                </div>
              ))}
            </div>
          )}
        </RecordCard>
      </div>

      <Card className="mt-5 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <WandSparkles className="size-4 text-primary" /> Open a Filing
          </CardTitle>
          <CardDescription>Pair one recorded Client with one published Rule Package.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => filingRef.send({ type: 'filing.open-dialog' })} data-jr-key="open-filing-open">
            Open Filing
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={clientDialogOpen}
        onOpenChange={(open) => {
          if (!open) ui.closeOverlay('openClientDialog');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open Client</DialogTitle>
            <DialogDescription>Record the estate or trust and its fiduciary.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="client-name">Client name</Label>
              <Input
                id="client-name"
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
                data-jr-key="open-client-clientName"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="fiduciary-name">Fiduciary name</Label>
              <Input
                id="fiduciary-name"
                value={fiduciaryName}
                onChange={(event) => setFiduciaryName(event.target.value)}
                data-jr-key="open-client-fiduciaryName"
              />
            </div>
            {clientError && <p className="text-sm text-destructive">{clientError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => ui.closeOverlay('openClientDialog')}>
              Cancel
            </Button>
            <Button
              onClick={openClient}
              disabled={!clientName.trim() || !fiduciaryName.trim()}
              data-jr-key="open-client"
            >
              Open Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublishPackageDialog
        open={packageDialogOpen}
        onClose={() => ui.closeOverlay('publishRulePackageDialog')}
        taxYear={taxYear}
        version={version}
        setTaxYear={setTaxYear}
        setVersion={setVersion}
        upload={upload}
        error={publishError}
        onPublish={publishPackage}
      />

      <Dialog
        open={filingDialogOpen}
        onOpenChange={(open) => {
          if (!open) ui.closeOverlay('openFilingDialog');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open Filing</DialogTitle>
            <DialogDescription>Select existing records; identifiers are never typed manually.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Client</Label>
              <Select
                items={clients.map((item) => ({ value: item.clientId, label: item.clientName ?? 'Unnamed Client' }))}
                value={selectedClientId}
                onValueChange={(value) => setSelectedClientId(typeof value === 'string' ? value : '')}
              >
                <SelectTrigger data-jr-key="open-filing-clientId">
                  <SelectValue placeholder="Choose a Client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((item) => (
                    <SelectItem key={item.clientId} value={item.clientId}>
                      {item.clientName ?? 'Unnamed Client'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Rule Package</Label>
              <Select
                items={packageItems.map((item) => ({
                  value: item.rulePackageId,
                  label: `${item.taxYear ?? 'Unknown year'} · ${item.version ?? 'Unknown version'}`,
                }))}
                value={selectedRulePackageId}
                onValueChange={(value) => setSelectedRulePackageId(typeof value === 'string' ? value : '')}
              >
                <SelectTrigger data-jr-key="open-filing-rulePackageId">
                  <SelectValue placeholder="Choose a Rule Package" />
                </SelectTrigger>
                <SelectContent>
                  {packageItems.map((item) => (
                    <SelectItem key={item.rulePackageId} value={item.rulePackageId}>
                      {item.taxYear ?? 'Unknown year'} · {item.version ?? 'Unknown version'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => ui.closeOverlay('openFilingDialog')}>
              Cancel
            </Button>
            <Button
              onClick={openFiling}
              disabled={!selectedClientId || !selectedRulePackageId}
              data-jr-key="open-filing"
            >
              Open Filing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppChrome>
  );
}

function PublishPackageDialog({
  open,
  onClose,
  taxYear,
  version,
  setTaxYear,
  setVersion,
  upload,
  error,
  onPublish,
}: {
  open: boolean;
  onClose: () => void;
  taxYear: string;
  version: string;
  setTaxYear: (value: string) => void;
  setVersion: (value: string) => void;
  upload: ReturnType<typeof useFileUpload>;
  error: string;
  onPublish: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Rule Package</DialogTitle>
          <DialogDescription>
            Upload the package, wait until it is stored, then record its tax year and version.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="tax-year">Tax year</Label>
            <Input
              id="tax-year"
              inputMode="numeric"
              value={taxYear}
              onChange={(event) => setTaxYear(event.target.value)}
              data-jr-key="publish-rule-package-taxYear"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="package-version">Version</Label>
            <Input
              id="package-version"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              data-jr-key="publish-rule-package-version"
            />
          </div>
          <div className="grid gap-2">
            <Label
              htmlFor="rule-package-file"
              className={buttonVariants({ variant: 'outline' })}
              data-jr-key="publish-rule-package-fileRef"
            >
              <Upload /> Choose package file
            </Label>
            <input
              id="rule-package-file"
              type="file"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload.pick(file);
              }}
            />
            {upload.status !== 'idle' && (
              <p className="text-xs text-muted-foreground">
                Upload: {upload.status}
                {upload.progress !== null ? ` (${Math.round(upload.progress * 100)}%)` : ''}
              </p>
            )}
            {upload.error && (
              <p className="text-sm text-destructive">The package file could not be stored. Try another file.</p>
            )}
            {upload.ref && <p className="text-xs text-primary">Stored and ready to publish.</p>}
            {upload.status === 'rejected' && (
              <Button size="sm" variant="outline" onClick={upload.reset}>
                Choose another file
              </Button>
            )}
          </div>
          {error && (
            <p className="text-sm text-destructive">The Rule Package could not be published. Please try again.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onPublish}
            disabled={upload.status !== 'stored' || !upload.ref}
            data-jr-key="publish-rule-package"
          >
            Publish Package
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="border-b">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}

function ClientRow({ client }: { client: SetupClient }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div>
        <p className="font-medium">{client.clientName ?? 'Unnamed Client'}</p>
        <p className="text-sm text-muted-foreground">Fiduciary: {client.fiduciaryName ?? 'Unavailable'}</p>
      </div>
      <FileCheck2 className="size-4 text-primary" />
    </div>
  );
}

function StateText({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-7 text-sm text-muted-foreground">
      <span>{text}</span>
      {action}
    </div>
  );
}

function PageMessage({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{detail}</CardDescription>
        </CardHeader>
      </Card>{' '}
    </div>
  );
}
